import './bespoke-rfq-emails.scss'

import { Button, Skeleton, Table, Typography } from 'antd'
import classnames from 'classnames'
import React, { useEffect, useState } from 'react'
import { Column, Row } from 'react-display-flex'
import useSWR from 'swr'

import { fetchByDomain, fetcher } from '../../../../api'
import { emptyCharacter } from '../../../../components/EmptyChar'
import { buildTableFilterKeys } from '../../../../components/tables/build-table-filter-keys'
import { getDefaultDomainByEnvironment } from '../../../../models'
import { getEnvironment } from '../../../../util/environment'
import { RfqEmail, transformRfqEmailsToTableItems } from '../../models'
import { CreateBespokeRfqEmailDrawer } from '../CreateBespokeRfqEmail'
import { DeleteBespokeRfqEmailModal } from '../DeleteBespokeRfqEmailModal'
import { UpdateBespokeRfqEmailDrawer } from '../UpdateBespokeRfqEmails'
import { buildRfqEmailsTableColumns } from './rfq-emails-table-columns'

export const BespokeRfqEmails = () => {
  const environment = getEnvironment()

  const { data: rfqEmails, mutate, error } = useSWR('/rfqs/emails', fetcher)
  const { data: tenants } = useSWR('/tenants', fetcher)
  const { data: issuers } = useSWR(['/issuers', getDefaultDomainByEnvironment(environment)], fetchByDomain)

  const [isCreateRfqEmailDrawerOpen, setIsCreateRfqEmailDrawerOpen] = useState(false)
  const [rfqEmailToUpdate, setRfqEmailToUpdate] = useState(null)
  const [rfqEmailToBeDeleted, setRfqEmailToBeDeleted] = useState(null)
  const [rfqEmailsTableItems, setRfqEmailsTableItems] = useState([])
  const [filters, setFilters] = useState({ instrumentCodes: [], displayNames: [] })

  useEffect(() => {
    if (rfqEmails === null || issuers === null || tenants === null) return

    const tableItems = transformRfqEmailsToTableItems(rfqEmails, issuers, tenants)
    setRfqEmailsTableItems(tableItems)
  }, [rfqEmails, issuers, tenants])

  useEffect(() => {
    rfqEmailsTableItems &&
      setFilters({
        instrumentCodes: buildTableFilterKeys(rfqEmailsTableItems, 'instrumentCode', formatInstrumentCodeFilter),
        displayNames: buildTableFilterKeys(rfqEmailsTableItems, 'name'),
      })
  }, [rfqEmailsTableItems])

  const columns = buildRfqEmailsTableColumns({
    onUpdate: setRfqEmailToUpdate,
    onDelete: setRfqEmailToBeDeleted,
    filters,
  })

  const closeDialogs = () => {
    setRfqEmailToUpdate(null)
    setRfqEmailToBeDeleted(null)
    setIsCreateRfqEmailDrawerOpen(false)
  }

  const onCreateOrUpdateRfqEmailSuccess = async () => {
    await mutate()
    closeDialogs()
  }

  return (
    <Column className="rfq-emails">
      <Typography.Title>Bespoke RFQ Emails</Typography.Title>

      {!rfqEmails && !!error ? (
        <Skeleton />
      ) : (
        <>
          <Row justifyContentEnd>
            <Button type="primary" onClick={() => setIsCreateRfqEmailDrawerOpen(true)}>
              Create RFQ Email
            </Button>
          </Row>

          <Table
            rowClassName={(rfqEmail: RfqEmail) =>
              classnames({ active: rfqEmailToUpdate && rfqEmail.id === rfqEmailToUpdate.id })
            }
            loading={rfqEmails === null || issuers === null || tenants === null}
            dataSource={rfqEmailsTableItems}
            columns={columns}
            pagination={false}
            rowKey="id"
            size="small"
          />
        </>
      )}

      <CreateBespokeRfqEmailDrawer
        issuers={issuers}
        tenants={tenants}
        isVisible={isCreateRfqEmailDrawerOpen}
        onClose={closeDialogs}
        onSuccess={onCreateOrUpdateRfqEmailSuccess}
      />

      <UpdateBespokeRfqEmailDrawer
        rfqEmail={rfqEmailToUpdate}
        issuers={issuers}
        tenants={tenants}
        isVisible={!!rfqEmailToUpdate}
        onClose={closeDialogs}
        onSuccess={onCreateOrUpdateRfqEmailSuccess}
      />

      <DeleteBespokeRfqEmailModal
        rfqEmail={rfqEmailToBeDeleted}
        isVisible={!!rfqEmailToBeDeleted}
        onClose={closeDialogs}
        onSuccess={onCreateOrUpdateRfqEmailSuccess}
      />
    </Column>
  )
}

const formatInstrumentCodeFilter = (instrumentCode: string): string =>
  instrumentCode === emptyCharacter ? 'Any' : instrumentCode
